import * as React from 'react';
import {
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';

import { cn } from '../../shared/lib/cn';
import { Button } from './button';
import { DataTable, type Column, type SortState } from './data-table';
import { Input } from './input';

export type TabularDataType = 'text' | 'number' | 'currency' | 'percent' | 'date';

export type TabularColumn<T extends Record<string, any>> = Column<T> & {
  type?: TabularDataType;
  csvHeader?: string;
  csvValue?: (row: T) => unknown;
};

export interface TabularDataSet {
  columns: string[];
  rows: unknown[][];
  row_count?: number;
}

export type TabularRows = TabularDataSet;

interface ObjectRowsTabularDataViewerProps<T extends Record<string, any>> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  data: T[];
  columns?: TabularColumn<T>[];
  getRowId?: (row: T) => string | number;
  exportFilename?: string;
  enableCsvExport?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  pageSize?: number;
  maxHeight?: string;
  compact?: boolean;
  stickyHeader?: boolean;
  className?: string;
  actions?: React.ReactNode;
}

interface DataSetTabularDataViewerProps {
  data: TabularDataSet;
  title?: React.ReactNode;
  description?: React.ReactNode;
  filename?: string;
  fileBaseName?: string;
  maxHeight?: string;
  dense?: boolean;
  paginated?: boolean;
  pageSize?: number;
  searchable?: boolean;
  exportable?: boolean;
  showExports?: boolean;
  loading?: boolean;
  emptyMessage?: string;
  className?: string;
}

export type TabularDataViewerProps<T extends Record<string, any> = Record<string, any>> =
  | ObjectRowsTabularDataViewerProps<T>
  | DataSetTabularDataViewerProps;

interface TabularViewerRow {
  id: string;
  cells: unknown[];
}

export function makeTabularRows(rows: Array<Record<string, unknown>>): TabularRows {
  const columns: string[] = [];
  const seen = new Set<string>();

  rows.forEach(row => {
    Object.keys(row).forEach(key => {
      if (!seen.has(key)) {
        seen.add(key);
        columns.push(key);
      }
    });
  });

  return {
    columns,
    rows: rows.map(row => columns.map(column => row[column] ?? null)),
    row_count: rows.length,
  };
}

export function tabularRowsToObjects(data: TabularDataSet): Array<Record<string, unknown>> {
  return data.rows.map(row =>
    data.columns.reduce<Record<string, unknown>>((acc, column, index) => {
      acc[column] = row[index] ?? null;
      return acc;
    }, {}),
  );
}

export function stringifyTabularCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') {
    return Number.isInteger(value)
      ? value.toLocaleString('en-US')
      : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

function rawTabularCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function csvEscape(value: unknown): string {
  const text = rawTabularCell(value);
  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function serializeTabularRowsToCsv(data: TabularDataSet): string {
  return [
    data.columns.map(csvEscape).join(','),
    ...data.rows.map(row => data.columns.map((_, index) => csvEscape(row[index])).join(',')),
  ].join('\n');
}

export function escapeCsvCell(value: unknown): string {
  const rawText = stringifyTabularCell(value);
  const text = /^[=+\-@\t\r]/.test(rawText) ? `'${rawText}` : rawText;
  return `"${text.replace(/"/g, '""')}"`;
}

export function buildTabularCsv(data: TabularDataSet): string;
export function buildTabularCsv<T extends Record<string, any>>(
  rows: T[],
  columns: TabularColumn<T>[],
): string;
export function buildTabularCsv<T extends Record<string, any>>(
  dataOrRows: TabularDataSet | T[],
  columns?: TabularColumn<T>[],
): string {
  if (Array.isArray(dataOrRows)) {
    const tableColumns = columns ?? inferColumns(dataOrRows);
    const headers = tableColumns.map(column => column.csvHeader ?? String(column.header ?? column.id));
    const body = dataOrRows.map(row =>
      tableColumns.map(column => escapeCsvCell(formatCsvValue(getColumnValue(row, column), column.type))),
    );

    return [headers.map(escapeCsvCell), ...body].map(values => values.join(',')).join('\n');
  }

  return [
    dataOrRows.columns.map(escapeCsvCell).join(','),
    ...dataOrRows.rows.map(row =>
      dataOrRows.columns.map((_, index) => escapeCsvCell(row[index])).join(','),
    ),
  ].join('\n');
}

export function TabularDataViewer<T extends Record<string, any>>(
  props: TabularDataViewerProps<T>,
) {
  if (isTabularDataSet(props.data)) {
    return <DataSetTabularDataViewer {...(props as DataSetTabularDataViewerProps)} />;
  }

  return <ObjectRowsTabularDataViewer {...(props as ObjectRowsTabularDataViewerProps<T>)} />;
}

function ObjectRowsTabularDataViewer<T extends Record<string, any>>({
  title,
  description,
  data,
  columns: providedColumns,
  getRowId = (row: T) => row.id ?? JSON.stringify(row),
  exportFilename = 'table.csv',
  enableCsvExport = true,
  loading = false,
  emptyMessage = 'No rows available',
  pageSize = 25,
  maxHeight,
  compact = false,
  stickyHeader = true,
  className,
  actions,
}: ObjectRowsTabularDataViewerProps<T>) {
  const [sortState, setSortState] = React.useState<SortState>({
    column: null,
    direction: null,
  });
  const [page, setPage] = React.useState(1);

  const columns = React.useMemo(
    () => providedColumns ?? inferColumns(data),
    [data, providedColumns],
  );

  React.useEffect(() => {
    setPage(1);
  }, [data, pageSize]);

  const sortedData = React.useMemo(
    () => sortRows(data, columns, sortState),
    [columns, data, sortState],
  );

  const handleExport = () => {
    downloadText(exportFilename, buildTabularCsv(sortedData, columns));
  };

  return (
    <div className={cn('space-y-3', className)}>
      {(title || description || enableCsvExport || actions) && (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            {title && (
              <h2 className="text-lg font-semibold text-charcoal dark:text-gray-100">
                {title}
              </h2>
            )}
            {description && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {description}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
              {data.length.toLocaleString()} rows
            </span>
            {actions}
            {enableCsvExport && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleExport}
                disabled={loading || data.length === 0}
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export CSV
              </Button>
            )}
          </div>
        </div>
      )}

      <DataTable
        data={sortedData}
        columns={columns}
        getRowId={getRowId}
        loading={loading}
        emptyMessage={emptyMessage}
        sortable
        sortState={sortState}
        onSortChange={setSortState}
        paginated={sortedData.length > pageSize}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        stickyHeader={stickyHeader}
        maxHeight={maxHeight}
        compact={compact}
        striped
      />
    </div>
  );
}

function DataSetTabularDataViewer({
  data,
  title,
  description,
  filename,
  fileBaseName,
  maxHeight = '420px',
  dense = false,
  paginated = true,
  pageSize = 25,
  searchable = true,
  exportable = true,
  showExports,
  loading = false,
  emptyMessage = 'No rows available.',
  className,
}: DataSetTabularDataViewerProps) {
  const [query, setQuery] = React.useState('');
  const [copyMessage, setCopyMessage] = React.useState<string | null>(null);
  const [sortState, setSortState] = React.useState<SortState>({ column: null, direction: null });
  const [page, setPage] = React.useState(1);

  const exportsEnabled = showExports ?? exportable;
  const csvFilename =
    filename ??
    `${fileBaseName || 'table-export'}-${new Date().toISOString().slice(0, 10)}.csv`;
  const exportBaseName = baseNameFrom(csvFilename);

  React.useEffect(() => {
    setPage(1);
  }, [query, sortState.column, sortState.direction, data.rows]);

  const tableRows = React.useMemo<TabularViewerRow[]>(
    () => data.rows.map((row, index) => ({ id: String(index), cells: row })),
    [data.rows],
  );

  const filteredRows = React.useMemo<TabularViewerRow[]>(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return tableRows;
    return tableRows.filter(row =>
      row.cells.some(cell => stringifyTabularCell(cell).toLowerCase().includes(normalizedQuery)),
    );
  }, [query, tableRows]);

  const sortedRows = React.useMemo<TabularViewerRow[]>(() => {
    if (!sortState.column || !sortState.direction) return filteredRows;
    const columnIndex = Number(sortState.column.replace(/^column-/, ''));
    if (Number.isNaN(columnIndex)) return filteredRows;

    return [...filteredRows].sort((left, right) => {
      const leftValue = left.cells[columnIndex];
      const rightValue = right.cells[columnIndex];
      const leftNumber = typeof leftValue === 'number' ? leftValue : Number.NaN;
      const rightNumber = typeof rightValue === 'number' ? rightValue : Number.NaN;
      const comparison =
        Number.isNaN(leftNumber) || Number.isNaN(rightNumber)
          ? stringifyTabularCell(leftValue).localeCompare(stringifyTabularCell(rightValue), undefined, {
              numeric: true,
              sensitivity: 'base',
            })
          : leftNumber - rightNumber;
      return sortState.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredRows, sortState]);

  const columns = React.useMemo<Column<TabularViewerRow>[]>(
    () =>
      data.columns.map((column, columnIndex) => ({
        id: `column-${columnIndex}`,
        header: column,
        sortable: true,
        align: data.rows.some(row => typeof row[columnIndex] === 'number') ? 'right' : 'left',
        minWidth: '140px',
        cell: ({ row }) => {
          const value = row.cells[columnIndex];
          const text = stringifyTabularCell(value);
          return (
            <span
              className={cn(
                'block max-w-[420px] truncate',
                typeof value === 'number' && 'font-mono text-xs tabular-nums',
              )}
              title={text}
            >
              {text || '-'}
            </span>
          );
        },
      })),
    [data.columns, data.rows],
  );

  const csvRows = React.useMemo(() => sortedRows.map(row => row.cells), [sortedRows]);
  const csv = React.useMemo(
    () => buildTabularCsv({ columns: data.columns, rows: csvRows }),
    [csvRows, data.columns],
  );

  async function copyCsv() {
    try {
      await navigator.clipboard.writeText(csv);
      setCopyMessage('Copied CSV');
    } catch {
      setCopyMessage('Clipboard unavailable');
    }
  }

  function exportJson() {
    downloadText(
      `${exportBaseName}.json`,
      JSON.stringify(tabularRowsToObjects({ columns: data.columns, rows: csvRows }), null, 2),
      'application/json;charset=utf-8',
    );
  }

  function exportExcel() {
    const worksheet = XLSX.utils.aoa_to_sheet([data.columns, ...csvRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
    XLSX.writeFile(workbook, `${exportBaseName}.xlsx`);
  }

  return (
    <div className={cn('flex min-w-0 flex-col gap-3', className)}>
      {(title || description || searchable || exportsEnabled) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            {title && <div className="text-sm font-semibold text-charcoal dark:text-gray-100">{title}</div>}
            {description && (
              <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-500 dark:text-gray-400">
                {description}
              </p>
            )}
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {filteredRows.length.toLocaleString()} of {(data.row_count ?? data.rows.length).toLocaleString()} rows
            </div>
          </div>
          <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
            {searchable && (
              <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={query}
                  onChange={event => setQuery(event.target.value)}
                  placeholder="Filter table..."
                  className="pl-9"
                />
              </div>
            )}
            {exportsEnabled && (
              <>
                <Button size="sm" variant="outline" onClick={copyCsv} disabled={!csvRows.length}>
                  <ClipboardDocumentIcon className="h-4 w-4" />
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadText(csvFilename, csv, 'text/csv;charset=utf-8')}
                  disabled={!csvRows.length}
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  CSV
                </Button>
                <Button size="sm" variant="outline" onClick={exportJson} disabled={!csvRows.length}>
                  JSON
                </Button>
                <Button size="sm" variant="outline" onClick={exportExcel} disabled={!csvRows.length}>
                  XLSX
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {copyMessage && (
        <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 dark:bg-dark-surface-2 dark:text-gray-300">
          {copyMessage}
        </div>
      )}
      <DataTable
        data={sortedRows}
        columns={columns}
        getRowId={row => row.id}
        loading={loading}
        sortable
        sortState={sortState}
        onSortChange={setSortState}
        stickyHeader
        maxHeight={maxHeight}
        compact={dense}
        paginated={paginated}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        emptyMessage={query.trim() ? 'No rows match this filter.' : emptyMessage}
      />
    </div>
  );
}

function inferColumns<T extends Record<string, any>>(data: T[]): TabularColumn<T>[] {
  const sample = data[0];
  if (!sample) return [];
  return Object.keys(sample).map(key => ({
    id: key,
    header: humanizeHeader(key),
    accessorKey: key as keyof T,
    sortable: true,
  }));
}

function sortRows<T extends Record<string, any>>(
  rows: T[],
  columns: TabularColumn<T>[],
  sortState: SortState,
): T[] {
  if (!sortState.column || !sortState.direction) return rows;
  const column = columns.find(item => item.id === sortState.column);
  if (!column) return rows;

  return [...rows].sort((left, right) => {
    const leftValue = getColumnValue(left, column);
    const rightValue = getColumnValue(right, column);
    const result = compareValues(leftValue, rightValue);
    return sortState.direction === 'asc' ? result : -result;
  });
}

function getColumnValue<T extends Record<string, any>>(row: T, column: TabularColumn<T>): unknown {
  if (column.csvValue) return column.csvValue(row);
  if (column.accessorFn) return column.accessorFn(row);
  if (column.accessorKey) return row[column.accessorKey];
  return '';
}

function compareValues(left: unknown, right: unknown): number {
  const leftNumber = Number(left);
  const rightNumber = Number(right);
  if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
    return leftNumber - rightNumber;
  }
  return String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

function formatCsvValue(value: unknown, type?: TabularDataType): string {
  if (value == null) return '';
  if (type === 'currency') return Number(value || 0).toFixed(2);
  if (type === 'percent') return `${Number(value || 0).toFixed(2)}%`;
  if (type === 'number') return String(Number(value || 0));
  return String(value);
}

function downloadText(filename: string, content: string, type = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(link.href);
  link.remove();
}

function baseNameFrom(filename: string) {
  return filename.replace(/\.(csv|json|xlsx)$/i, '');
}

function isTabularDataSet(data: unknown): data is TabularDataSet {
  return (
    typeof data === 'object'
    && data !== null
    && !Array.isArray(data)
    && Array.isArray((data as TabularDataSet).columns)
    && Array.isArray((data as TabularDataSet).rows)
  );
}

function humanizeHeader(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, match => match.toUpperCase());
}
